import * as t from 'io-ts';

import {RActivityIdRef} from './activityModel';
import {makeEnumRuntimeType} from './apiTypes';

/*
 * Runtime type.
 */

export enum AnnotationTypesEnum {
  HIGHLIGHT = 'highlight', // Highlight message body text
  HIGHLIGHT_COMMENT = 'highlight_comment', // Highlight comment body text
  SURVEY = 'survey',
}

export enum AnnotationSourcesEnum {
  LIGHT_BODY = 'lightBody',
  FULL_BODY = 'fullBody',
}

const RAnnotationBase = t.partial({
  commentActivityId: RActivityIdRef,
  createdAt: t.Integer,
});

const RHighlightAnnotationDirty = t.intersection([
  RAnnotationBase,
  t.type({
    type: t.literal(AnnotationTypesEnum.HIGHLIGHT),
    startIndex: t.Integer,
    endIndex: t.Integer,
    text: t.string,
    textBefore: t.string,
    textAfter: t.string,
    source: makeEnumRuntimeType(AnnotationSourcesEnum),
  }),
]);

const RSurveyAnnotationDirty = t.intersection([
  RAnnotationBase,
  t.type({
    type: t.literal(AnnotationTypesEnum.SURVEY),
    surveyType: t.union([t.string, t.undefined]),
    score: t.Integer,
    note: t.string,
    contactHandle: t.union([t.string, t.undefined]),
    contactId: t.union([t.Integer, t.undefined]),
  }),
]);

export const RHighlightCommentAnnotationDirty = t.intersection([
  RAnnotationBase,
  t.type({
    type: t.literal(AnnotationTypesEnum.HIGHLIGHT_COMMENT),
    startIndex: t.Integer,
    endIndex: t.Integer,
    text: t.string,
    textBefore: t.string,
    textAfter: t.string,
  }),
]);

const RAnnotationDirty = t.readonly(
  t.taggedUnion('type', [
    RHighlightAnnotationDirty,
    RSurveyAnnotationDirty,
    RHighlightCommentAnnotationDirty,
  ]),
  'annotation',
);

/*
 * Type.
 */

export interface AnnotationHighlightModel extends t.TypeOf<typeof RHighlightAnnotationDirty> {}
export interface AnnotationSurveyModel extends t.TypeOf<typeof RSurveyAnnotationDirty> {}
export interface AnnotationHighlightCommentModel extends t.TypeOf<typeof RHighlightCommentAnnotationDirty> {}
export type AnnotationModel =
  | AnnotationHighlightModel
  | AnnotationSurveyModel
  | AnnotationHighlightCommentModel;
export const RHighlightAnnotation = t.clean<AnnotationHighlightModel>(RHighlightAnnotationDirty as any);
export const RHighlightCommentAnnotation = t.clean<AnnotationHighlightModel>(
  RHighlightCommentAnnotationDirty as any,
);
export const RSurveyAnnotation = t.clean<AnnotationSurveyModel>(RSurveyAnnotationDirty as any);
export const RAnnotation = t.clean<AnnotationModel>(RAnnotationDirty as any);

/*
 * IO methods.
 */

export function parseAnnotation(src: any): AnnotationModel | undefined {
  if (src.type === AnnotationTypesEnum.SURVEY) {
    return {
      type: AnnotationTypesEnum.SURVEY,
      commentActivityId: src.comment_activity_id || undefined,
      surveyType: src.survey_type || undefined,
      score: src.score,
      note: src.note,
      contactHandle: src.contact_handle || undefined,
      contactId: src.contact_id || undefined,
      createdAt: src.created_at,
    };
  }

  if (src.type === AnnotationTypesEnum.HIGHLIGHT) {
    return {
      type: AnnotationTypesEnum.HIGHLIGHT,
      commentActivityId: src.comment_activity_id || undefined,
      startIndex: src.start_index,
      endIndex: src.end_index,
      text: src.text,
      textBefore: src.text_before || '',
      textAfter: src.text_after || '',
      source: src.source,
      createdAt: src.created_at,
    };
  }

  if (src.type === AnnotationTypesEnum.HIGHLIGHT_COMMENT) {
    return {
      type: AnnotationTypesEnum.HIGHLIGHT_COMMENT,
      commentActivityId: src.comment_activity_id || undefined,
      startIndex: src.start_index,
      endIndex: src.end_index,
      text: src.text,
      textBefore: src.text_before || '',
      textAfter: src.text_after || '',
      createdAt: src.created_at,
    };
  }

  // gracefully handle the case where a new
  // annotation is introduced on the backend
  return undefined;
}

export function serializeAnnotation(annotation: AnnotationModel) {
  if (annotation.type === AnnotationTypesEnum.SURVEY) {
    return {
      type: annotation.type,
      survey_type: annotation.surveyType,
      score: annotation.score,
      note: annotation.note,
      contactHandle: annotation.contactHandle,
      contactId: annotation.contactId,
      created_at: annotation.createdAt,
    };
  }

  if (annotation.type === AnnotationTypesEnum.HIGHLIGHT_COMMENT) {
    return {
      type: annotation.type,
      start_index: annotation.startIndex,
      end_index: annotation.endIndex,
      text: annotation.text,
      text_before: annotation.textBefore,
      text_after: annotation.textAfter,
      created_at: annotation.createdAt,
    };
  }

  return {
    type: annotation.type,
    start_index: annotation.startIndex,
    end_index: annotation.endIndex,
    text: annotation.text,
    text_before: annotation.textBefore,
    text_after: annotation.textAfter,
    source: annotation.source,
    created_at: annotation.createdAt,
  };
}
